import { buildSystemPrompt, INSIGHT_PROMPTS } from '../data/aiContext';
import { apiFetch } from './api';
import type { Assumptions, PackageConfig, TabId } from '../types';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface SimState {
  assumptions: Assumptions;
  packages: PackageConfig[];
  activeTab: TabId;
  selectedPrice: number;
}

export async function streamChat(
  messages: ChatMessage[],
  state: SimState,
  onChunk: (text: string) => void,
  onDone: () => void,
  onError: (err: string) => void,
): Promise<void> {
  const system = buildSystemPrompt(state);

  try {
    const res = await apiFetch('/api/chat', {
      method: 'POST',
      body: JSON.stringify({ messages, system }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Server error' }));
      onError(err.error || `HTTP ${res.status}`);
      return;
    }

    const reader = res.body?.getReader();
    if (!reader) { onError('No response stream'); return; }

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'text') onChunk(data.text);
          else if (data.type === 'done') onDone();
          else if (data.type === 'error') onError(data.error);
        } catch {
          // skip malformed lines
        }
      }
    }
    onDone();
  } catch (err) {
    onError(err instanceof Error ? err.message : 'Network error');
  }
}

export async function getInsight(
  tabId: string,
  state: SimState,
): Promise<string> {
  const system = buildSystemPrompt(state);
  const prompt = INSIGHT_PROMPTS[tabId];
  if (!prompt) return '';

  const res = await apiFetch('/api/insights', {
    method: 'POST',
    body: JSON.stringify({ prompt, system }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Server error' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  const data = await res.json();
  return data.content || '';
}

export function getStateHash(state: SimState): string {
  return JSON.stringify({
    a: state.assumptions,
    p: state.packages.map(p => ({ pr: p.price, f: [...p.enabledFeatures].sort() })),
    sp: state.selectedPrice,
  });
}

const ALL_INSIGHT_TABS = ['summary', 'builder', 'wtp', 'price', 'features', 'segments', 'financial'];

export async function prefetchAllInsights(
  state: SimState,
  currentCache: Record<string, { hash: string; content: string }>,
  setCached: (tabId: string, hash: string, content: string) => void,
): Promise<void> {
  const hash = getStateHash(state);

  const tabsToFetch = ALL_INSIGHT_TABS.filter(tabId => {
    const cached = currentCache[tabId];
    return !cached || cached.hash !== hash;
  });

  if (tabsToFetch.length === 0) return;

  await Promise.allSettled(
    tabsToFetch.map(async (tabId) => {
      try {
        const result = await getInsight(tabId, state);
        setCached(tabId, hash, result);
      } catch {
        // silently skip failed tabs
      }
    })
  );
}
